import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Upload, Trash2, Eye, Plus, CheckCircle2, XCircle, Image as ImageIcon, FileText } from 'lucide-react';
import { formatINR, maskAcct } from './payoutUtils';
import { openSecure } from './SettlementDetails';

const METHODS = [
  { key: 'screenshot', label: 'Screenshot Upload', desc: 'Upload one or more payment screenshots / PDF proofs' },
  { key: 'utr', label: 'UTR Number', desc: 'Enter one or more UTRs whose amounts total the payout' },
  { key: 'excel', label: 'Excel Upload', desc: 'Use the Excel Settlement tool on the Payouts page for file-based settlement' },
];

// "Complete Payout" — settlement proof collection + validation. Payout completes ONLY after validation passes.
export const SettlementModal = ({ payout, open, onOpenChange, onCompleted }) => {
  const { api } = useAuth();
  const [method, setMethod] = useState('screenshot');
  const [state, setState] = useState(null);
  const [utrs, setUtrs] = useState([{ utr: '', amount: '' }]);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const fileRef = useRef(null);

  const load = async () => { const r = await api.get(`/payout-settlements/${payout.id}`); setState(r.data); return r.data; };
  useEffect(() => {
    if (!open || !payout) return;
    setMethod('screenshot'); setUtrs([{ utr: '', amount: String(payout.amount ?? '') }]); setValidation(null); setConfirm(false);
    load().catch(() => toast.error('Unable to load settlement state'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payout?.id]);

  const total = utrs.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const diff = Math.round(((payout?.amount || 0) - total) * 100) / 100;

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('files', f));
    setUploading(true);
    try {
      const r = await api.post(`/payout-settlements/${payout.id}/proofs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setState(r.data);
      if (r.data.errors?.length) toast.error(r.data.errors.join('; '));
      else toast.success(`${r.data.added.length} file(s) uploaded`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const removeProof = async (id) => {
    try { await api.delete(`/payout-settlements/${payout.id}/proofs/${id}`); await load(); } catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };

  const validate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/payout-settlements/${payout.id}/validate`, { method, utrs: method === 'utr' ? utrs.map(u => ({ utr: u.utr, amount: parseFloat(u.amount) || 0 })) : [] });
      setValidation(r.data);
      if (r.data.valid) setConfirm(true); else toast.error(r.data.errors[0]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Validation failed'); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/payout-settlements/${payout.id}/complete`,
        { method, utrs: method === 'utr' ? utrs.map(u => ({ utr: u.utr, amount: parseFloat(u.amount) || 0 })) : [] },
        { headers: { 'Idempotency-Key': `settle-${payout.id}-${Date.now()}` } });
      toast.success(`Payout ${payout.order_id} completed`);
      onCompleted?.(r.data); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Completion failed'); setConfirm(false); await load().catch(() => {}); }
    finally { setBusy(false); }
  };

  if (!payout) return null;
  const proofs = state?.proofs || [];
  const canSubmit = method === 'screenshot' ? proofs.length > 0 : method === 'utr' ? utrs.every(u => u.utr.trim() && parseFloat(u.amount) > 0) && diff === 0 : false;

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[92vh] overflow-y-auto" data-testid="settlement-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Complete Payout</DialogTitle>
          <DialogDescription>Collect and validate settlement proof. The payout becomes Completed only after validation passes.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="settlement-payout-summary">
          {[['Order ID', payout.order_id, 'font-mono'], ['Merchant', payout.merchant?.business_name || '—'], ['Account Holder', payout.beneficiary_name], ['Bank', payout.bank_name],
            ['Account', maskAcct(payout.account_number_masked), 'font-mono'], ['IFSC', payout.ifsc_code, 'font-mono'], ['Requested Amount', formatINR(payout.amount, 2), 'font-mono text-primary'], ['Current Status', payout.status.replace(/_/g, ' ')]].map(([k, v, cls]) => (
            <div key={k} className="p-2 rounded-sm border border-border/50"><p className="text-[10px] uppercase text-muted-foreground">{k}</p><p className={`font-medium truncate ${cls || ''}`}>{v}</p></div>
          ))}
        </div>

        {!confirm ? (
          <>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Settlement Method</p>
              <div className="grid sm:grid-cols-3 gap-2" role="radiogroup">
                {METHODS.map(m => (
                  <button key={m.key} type="button" role="radio" aria-checked={method === m.key} onClick={() => { setMethod(m.key); setValidation(null); }}
                    className={`text-left p-3 rounded-sm border transition-colors ${method === m.key ? 'border-primary bg-primary/5' : 'border-border/60 hover:bg-accent/40'}`} data-testid={`method-${m.key}`}>
                    <p className="text-sm font-medium flex items-center gap-2"><span className={`w-3 h-3 rounded-full border ${method === m.key ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />{m.label}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{m.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {method === 'screenshot' && (
              <div className="space-y-2" data-testid="screenshot-section">
                <div className="flex items-center justify-between"><p className="text-sm font-medium">Settlement Proof <span className="text-xs text-muted-foreground">({proofs.length}/10)</span></p>
                  <input ref={fileRef} type="file" multiple accept=".png,.jpg,.jpeg,.pdf" className="hidden" onChange={e => upload(e.target.files)} data-testid="proof-file-input" />
                  <Button size="sm" variant="outline" className="rounded-sm" disabled={uploading || proofs.length >= 10} onClick={() => fileRef.current?.click()} data-testid="upload-proof-btn">{uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Upload Screenshot</Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Supported: PNG, JPG, JPEG, PDF · Max 10MB each · Multiple files allowed</p>
                {proofs.length === 0 && <div className="p-6 border border-dashed border-border/60 rounded-sm text-center text-sm text-muted-foreground">No proof uploaded yet</div>}
                {proofs.map(p => (
                  <div key={p.id} className="flex items-center justify-between gap-2 p-2 rounded-sm border border-border/50 text-sm" data-testid={`proof-item-${p.id}`}>
                    <div className="flex items-center gap-2 min-w-0">{p.is_image ? <ImageIcon className="h-4 w-4 text-muted-foreground" /> : <FileText className="h-4 w-4 text-muted-foreground" />}<span className="truncate text-xs">{p.original_filename}</span><span className="text-[10px] text-muted-foreground">{Math.max(1, Math.round(p.size / 1024))} KB</span></div>
                    <div className="flex gap-1"><Button variant="ghost" size="sm" className="h-7 px-2 rounded-sm text-xs" onClick={() => openSecure(api, p.view_url)} data-testid={`proof-preview-${p.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button><Button variant="ghost" size="sm" className="h-7 px-2 rounded-sm text-xs text-red-600" onClick={() => removeProof(p.id)} data-testid={`proof-remove-${p.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</Button></div>
                  </div>
                ))}
              </div>
            )}

            {method === 'utr' && (
              <div className="space-y-2" data-testid="utr-section">
                <div className="rounded-sm border border-border/50 overflow-hidden">
                  <div className="grid grid-cols-[1fr_140px_40px] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/40"><span>UTR Number</span><span>Amount (₹)</span><span /></div>
                  {utrs.map((u, i) => {
                    const vr = validation?.utrs?.[i];
                    return (
                      <div key={i} className="px-3 py-2 border-t border-border/40">
                        <div className="grid grid-cols-[1fr_140px_40px] gap-2 items-center">
                          <Input value={u.utr} onChange={e => { const n = [...utrs]; n[i] = { ...n[i], utr: e.target.value.trim() }; setUtrs(n); setValidation(null); }} placeholder="e.g. 000547670110" className="h-8 rounded-sm font-mono text-sm" data-testid={`utr-input-${i}`} />
                          <Input type="number" value={u.amount} onChange={e => { const n = [...utrs]; n[i] = { ...n[i], amount: e.target.value }; setUtrs(n); setValidation(null); }} className="h-8 rounded-sm font-mono text-sm" data-testid={`utr-amount-${i}`} />
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600" disabled={utrs.length === 1} onClick={() => setUtrs(utrs.filter((_, j) => j !== i))} data-testid={`utr-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                        {vr && !vr.ok && <p className="text-[11px] text-red-600 mt-1" data-testid={`utr-error-${i}`}>{vr.issues.join(' · ')}</p>}
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between px-3 py-2 border-t border-border/40 bg-muted/30 text-sm">
                    <Button variant="ghost" size="sm" className="h-7 rounded-sm text-xs" onClick={() => setUtrs([...utrs, { utr: '', amount: diff > 0 ? String(diff) : '' }])} data-testid="add-utr-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add Another UTR</Button>
                    <span>Total: <span className="font-mono font-semibold" data-testid="utr-total">{formatINR(total, 2)}</span></span>
                  </div>
                </div>
                {diff > 0 && <p className="text-xs text-amber-600" data-testid="utr-mismatch">Amount mismatch — {formatINR(diff, 2)} remaining</p>}
                {diff < 0 && <p className="text-xs text-red-600" data-testid="utr-exceeds">UTR amount exceeds payout amount</p>}
                {diff === 0 && total > 0 && <p className="text-xs text-green-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Totals match</p>}
              </div>
            )}

            {method === 'excel' && <div className="p-4 rounded-sm border border-border/60 text-sm text-muted-foreground" data-testid="excel-hint">Excel settlement handles many payouts at once. Close this dialog and use <span className="font-medium text-foreground">Excel Settlement</span> on the Payouts page to upload, match, validate and confirm.</div>}

            {validation && !validation.valid && (
              <div className="p-3 rounded-sm border border-red-500/40 bg-red-500/5 text-xs text-red-600 space-y-0.5" data-testid="validation-errors">{validation.errors.map((e, i) => <p key={i} className="flex items-start gap-1"><XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{e}</p>)}</div>
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="settlement-cancel-btn">Cancel</Button>
              <Button className="rounded-sm" disabled={busy || !canSubmit || method === 'excel'} onClick={validate} data-testid="settlement-validate-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Validate & Review</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-2" data-testid="settlement-summary">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Settlement Summary</p>
              {[['Payout Amount', formatINR(validation.payout_amount, 2)], ['Settlement Amount', formatINR(validation.settlement_amount, 2)], ['Method', METHODS.find(m => m.key === method)?.label], ['UTR Count', method === 'utr' ? validation.utr_count : '—'], ['Proof Files', validation.proof_files], ['Validation', <span className="text-green-600 inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" />Passed</span>]].map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-1.5 border-b border-border/40"><span className="text-muted-foreground">{k}</span><span className="font-medium font-mono">{v}</span></div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setConfirm(false)} disabled={busy} data-testid="settlement-back-btn">Back</Button>
              <Button className="rounded-sm bg-green-600 hover:bg-green-700 text-white" disabled={busy} onClick={complete} data-testid="settlement-complete-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Complete Payout</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
